// src/features/chat/panel/PanelThreadView.tsx
// Thread slide-over inside the entity panel: parent message up top, replies
// below, its own composer. ReplyInThread lands directly; live updates come
// from the same conv channel the stream subscribes to (WS_CHAT_ENABLED).

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import { ListThreadReplies } from '@core/api/endpoints/chat/list_thread_replies';
import { ReplyInThread } from '@core/api/endpoints/chat/reply_in_thread';
import { EntityAvatar } from '@/components/EntityAvatar';
import { attachmentApi } from '@core/api';
import type { AttachmentPreview } from '@core/types/chat';
import { wsClient, WS_CHAT_ENABLED } from '../wsClient';
import { ghostIconBtn, messageId, messageTs, renderLinkedText } from './panelShared';
import {
  MessageAttachments,
  PanelMessageActionButton,
  PanelMessageAnchor,
  type PanelMessageAction,
  type PanelMessageActionFor,
} from './PanelMessageList';

function timeLabel(ts: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(ts));
}

export function PanelThreadView({
  conversationId,
  parentMessage,
  memberNames,
  myProfileId,
  locale,
  onClose,
  resolveAttachmentUrl,
  onOpenImage,
  onDownload,
  renderAnchor,
  onAnchor,
  messageActionFor,
  messageActionLoadingId,
  onMessageAction,
}: {
  conversationId: string;
  parentMessage: any;
  memberNames: Map<string, string>;
  myProfileId: string | null;
  locale: string;
  onClose: () => void;
  resolveAttachmentUrl: (attachmentId: string) => Promise<string>;
  onOpenImage: (attachmentId: string) => void;
  onDownload: (attachmentId: string, fileName: string) => void;
  renderAnchor?: (message: any) => ReactNode;
  onAnchor?: (message: any) => void;
  messageActionFor?: PanelMessageActionFor;
  messageActionLoadingId?: string | null;
  onMessageAction?: (message: any, action: PanelMessageAction) => void;
}) {
  const { t } = useI18n();
  const [replies, setReplies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const parentId: string = messageId(parentMessage);
  const senderName = (msg: any): string => {
    const pid: string = msg?.sender?.entity?.id ?? '';
    return memberNames.get(pid) || msg?.sender?.display_name?.trim() || t('chat.entityPanel.unknownSender');
  };

  const loadReplies = useCallback(async () => {
    if (!parentId) return;
    setIsLoading(true);
    try {
      const res: any = await ListThreadReplies(conversationId, parentId);
      const raw: any = res?.messages;
      const items: any[] = Array.isArray(raw) ? raw : (raw?.messages ?? []);
      setReplies(items);
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, parentId]);

  useEffect(() => { void loadReplies(); }, [loadReplies]);

  // Lazy-hydrate attachment previews on replies (same gap as the stream:
  // ListThreadReplies returns entity refs only).
  const hydrated = useRef<Set<string>>(new Set());
  useEffect(() => {
    const pending = replies.filter((m) => {
      const mid = messageId(m);
      const ents = m?.attachments?.entities ?? [];
      const previews = m?.attachment_previews?.items ?? [];
      return mid && ents.length > 0 && previews.length < ents.length && !hydrated.current.has(mid);
    });
    if (pending.length === 0) return;
    for (const m of pending) hydrated.current.add(messageId(m));
    let cancelled = false;
    (async () => {
      for (const m of pending) {
        const previews: AttachmentPreview[] = [];
        for (const e of m.attachments.entities) {
          const aid = e?.id;
          if (!aid) continue;
          try {
            const res: any = await attachmentApi.GetAttachment(aid);
            const att = res?.attachment;
            if (!att?.entity?.id) continue;
            previews.push({
              attachment: att.entity,
              file_name: att.file_name,
              mime_type: att.mime_type,
              size_bytes: att.size_bytes,
              type: att.attachment_type,
              thumbnail_url: att.media?.thumbnail_url,
            } as AttachmentPreview);
          } catch {
            // skip failed items
          }
        }
        if (cancelled || previews.length === 0) continue;
        const mid = messageId(m);
        setReplies((prev) => prev.map((r) => (
          messageId(r) === mid ? { ...r, attachment_previews: { items: previews } } : r
        )));
      }
    })();
    return () => { cancelled = true; };
  }, [replies]);

  // Live thread replies on the same conversation channel.
  useEffect(() => {
    if (!WS_CHAT_ENABLED || !parentId) return;
    let cancelled = false;
    const unsub = wsClient.subscribe('conv:' + conversationId, {
      onThreadReply: (data: any) => {
        if (cancelled || !data) return;
        if (data.parent_message_id !== parentId && data.thread_id !== parentId) return;
        setReplies((prev) => {
          const rid = messageId(data);
          if (rid && prev.some((r) => messageId(r) === rid)) return prev;
          return [...prev, data];
        });
      },
      onMessageEdited: (data: any) => {
        if (cancelled || !data) return;
        if (data.parent_message_id !== parentId && data.thread_id !== parentId) return;
        setReplies((prev) => prev.map((r) => (messageId(r) === messageId(data) ? data : r)));
      },
      onMessageDeleted: (p: { message_id: string }) => {
        if (cancelled || !p?.message_id) return;
        setReplies((prev) => prev.filter((r) => messageId(r) !== p.message_id));
      },
    });
    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [conversationId, parentId]);

  const sendReply = async () => {
    const content = replyText.trim();
    if (!content || !parentId || isSending) return;
    setIsSending(true);
    try {
      const res: any = await ReplyInThread(conversationId, parentId, {
        parent_message_id: parentId,
        content,
      } as any);
      const msg = res?.message ?? res;
      if (msg && messageId(msg)) {
        setReplies((prev) =>
          prev.some((r) => messageId(r) === messageId(msg)) ? prev : [...prev, msg],
        );
      }
      setReplyText('');
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    } catch {
      // silent — the text stays in the composer
    } finally {
      setIsSending(false);
    }
  };

  const renderRow = (msg: any, showName: boolean, showAnchor = false) => {
    const ts = messageTs(msg);
    const name = senderName(msg);
    const pid: string = msg?.sender?.entity?.id ?? '';
    const isOwn = Boolean(myProfileId) && pid === myProfileId;
    return (
      <div key={messageId(msg) || ts} className="flex gap-2 py-1.5">
        <EntityAvatar name={name} image={msg?.sender?.avatar} profileId={pid} size="xs" className="h-7 w-7" />
        <div className={`min-w-0 flex-1 rounded-md px-2 py-1 ${isOwn ? 'bg-muted' : ''}`}>
          {showName && (
            <div className="flex items-baseline gap-2">
              <span className="truncate text-sm font-medium text-foreground">{name}</span>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {ts ? timeLabel(ts, locale) : ''}
              </span>
            </div>
          )}
          {showAnchor && <PanelMessageAnchor message={msg} renderAnchor={renderAnchor} onAnchor={onAnchor} />}
          {msg?.content && (
            <p className="whitespace-pre-wrap break-words text-sm text-foreground">
              {renderLinkedText(msg.content)}
            </p>
          )}
          {showAnchor && (
            <PanelMessageActionButton
              message={msg}
              action={messageActionFor?.(msg)}
              loadingId={messageActionLoadingId}
              onAction={onMessageAction}
            />
          )}
          {(msg?.attachment_previews?.items?.length ?? 0) > 0 && (
            <MessageAttachments
              previews={msg.attachment_previews.items}
              resolveUrl={resolveAttachmentUrl}
              onOpenImage={onOpenImage}
              onDownload={onDownload}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="absolute inset-0 z-10 flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center gap-1 border-b border-border px-2 py-1.5">
        <button
          type="button"
          onClick={onClose}
          className={ghostIconBtn}
          aria-label={t('chat.entityPanel.backToStream')}
          title={t('chat.entityPanel.backToStream')}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
        </button>
        <h4 className="text-sm font-semibold text-foreground">{t('chat.entityPanel.thread')}</h4>
      </div>

      {/* Parent message */}
      <div className="shrink-0 border-b border-border px-2 py-1">{renderRow(parentMessage, true, true)}</div>

      {/* Replies */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-2">
        {isLoading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : replies.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('chat.entityPanel.noRepliesYet')}
          </p>
        ) : (
          replies.map((r) => renderRow(r, true))
        )}
      </div>

      {/* Composer */}
      <div className="shrink-0 border-t border-border p-2">
        <div className="flex items-end gap-1.5">
          <textarea
            value={replyText}
            rows={1}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void sendReply();
              }
            }}
            placeholder={t('chat.entityPanel.threadComposerPlaceholder')}
            className="min-h-[2.25rem] flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('chat.entityPanel.threadComposerPlaceholder')}
          />
          <Button
            variant="primary"
            size="sm"
            isLoading={isSending}
            disabled={!replyText.trim() || isSending}
            onClick={() => void sendReply()}
          >
            {t('chat.entityPanel.send')}
          </Button>
        </div>
      </div>
    </div>
  );
}
